import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { voucherAPI, categoryAPI } from '../lib/api';
import { StatusBadge } from './DashboardPage';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, FileText } from 'lucide-react';
import { toast } from 'sonner';

export default function VoucherListPage() {
  const [vouchers, setVouchers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ status: '', voucherType: '', category: '', search: '' });

  const fetchVouchers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.voucherType) params.voucherType = filters.voucherType;
      if (filters.category) params.category = filters.category;
      if (filters.search) params.search = filters.search;

      const res = await voucherAPI.list(params);
      setVouchers(res.data.vouchers);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Failed to load vouchers');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    categoryAPI.list().then(res => setCategories(res.data.categories)).catch(() => toast.error('Failed to load categories'));
  }, []);

  useEffect(() => {
    const timer = setTimeout(fetchVouchers, 300);
    return () => clearTimeout(timer);
  }, [fetchVouchers]);

  const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });

  return (
    <div className="space-y-4" data-testid="voucher-list-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold tracking-tight text-slate-900">All Vouchers</h1>
        <Link
          to="/vouchers/new"
          className="bg-slate-900 text-white text-xs px-3 py-1.5 rounded-md hover:bg-slate-800 inline-flex items-center gap-1"
          data-testid="add-voucher-link"
        >
          <FileText className="w-3.5 h-3.5" /> New Voucher
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap gap-2" data-testid="voucher-filters">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-slate-400" />
          <Input
            placeholder="Search ID or description..."
            value={filters.search}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
            className="pl-8 h-8 text-xs"
            data-testid="search-vouchers-input"
          />
        </div>
        <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {['Draft', 'Pending', 'Approved', 'Rejected', 'Settled', 'Cancelled'].map(s =>
              <SelectItem key={s} value={s}>{s}</SelectItem>
            )}
          </SelectContent>
        </Select>
        <Select value={filters.voucherType} onValueChange={(v) => setFilters(f => ({ ...f, voucherType: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="type-filter">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {['Payment Voucher', 'Receipt Voucher', 'Contra Voucher', 'Journal Voucher', 'Advance Voucher', 'Advance Settlement Voucher'].map(t =>
              <SelectItem key={t} value={t}>{t}</SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="voucher-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Voucher ID</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase hidden sm:table-cell">Type</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase hidden md:table-cell">Date</th>
                    <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Amount</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Status</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase hidden lg:table-cell">Category</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase hidden lg:table-cell">Created By</th>
                  </tr>
                </thead>
                <tbody>
                  {vouchers.map((v) => (
                    <tr key={v._id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`voucher-row-${v.voucherId}`}>
                      <td className="px-4 py-2.5">
                        <Link to={`/vouchers/${v._id}`} className="text-xs font-medium text-slate-900 hover:underline" data-testid={`voucher-link-${v.voucherId}`}>
                          {v.voucherId}
                        </Link>
                        <p className="text-[10px] text-slate-400 truncate max-w-[150px] sm:hidden">{v.voucherType}</p>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-slate-600 hidden sm:table-cell">{v.voucherType}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-500 hidden md:table-cell">{formatDate(v.date)}</td>
                      <td className="px-4 py-2.5 text-xs font-medium text-slate-900 text-right">₹{v.amount?.toLocaleString('en-IN')}</td>
                      <td className="px-4 py-2.5"><StatusBadge status={v.status} /></td>
                      <td className="px-4 py-2.5 text-xs text-slate-500 hidden lg:table-cell">{v.category?.name || '-'}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-500 hidden lg:table-cell">{v.createdBy?.name || '-'}</td>
                    </tr>
                  ))}
                  {vouchers.length === 0 && (
                    <tr><td colSpan="7" className="text-center py-12 text-sm text-slate-400">No vouchers found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="border-t border-slate-200 px-4 py-2 flex items-center justify-between text-xs text-slate-500">
              <span>Showing {vouchers.length} of {total} vouchers</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
